import { useState, type CSSProperties, type ReactNode, type ComponentType } from 'react';
import { Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// Shared grid primitives — the page-agnostic building blocks of the
// Operations-Deck list pages (extracted from CompaniesPage so every
// converted page reuses the exact same look + behavior).
// ─────────────────────────────────────────────────────────────

/** A plain table cell — body font, single uniform color, optional truncation.
 *  When `ellipsis` is set and the content is a plain string, a native hover
 *  tooltip (title) is added automatically so the full, truncated value is
 *  still readable. Pass `title` to override. */
export function Cell({
  children,
  ellipsis = false,
  style,
  title,
}: {
  children: ReactNode;
  ellipsis?: boolean;
  style?: CSSProperties;
  title?: string;
}) {
  const autoTitle = title ?? (ellipsis && typeof children === 'string' ? children : undefined);
  return (
    <div
      title={autoTitle}
      style={{
        fontFamily: 'var(--font-body)',
        color: 'var(--color-text)',
        fontSize: 14,
        fontWeight: 500,
        whiteSpace: ellipsis ? 'nowrap' : undefined,
        overflow: ellipsis ? 'hidden' : undefined,
        textOverflow: ellipsis ? 'ellipsis' : undefined,
        minWidth: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Active / Inactive status pill — shared across grids (Inventory, Companies). */
export function ActivePill({ active }: { active: boolean }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 12,
      background: active ? 'rgba(26,143,90,0.12)' : 'var(--color-panel-alt)',
      border: `1px solid ${active ? 'rgba(26,143,90,0.30)' : 'var(--color-border)'}`,
      color: active ? 'var(--color-good)' : 'var(--color-text-dim)',
      fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-body)',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: active ? 'var(--color-good)' : 'var(--color-text-dim)', display: 'inline-block' }} />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

/** Prominent row action button — solid-fill at idle (client request), darker on hover. */
export function IconButton({
  children,
  onClick,
  title,
  hoverDanger,
  size = 36,
}: {
  children: ReactNode;
  onClick: () => void;
  title: string;
  hoverDanger?: boolean;
  size?: number;
}) {
  const [hover, setHover] = useState(false);
  const idleBg     = hoverDanger ? 'var(--color-bad)' : 'var(--color-accent)';
  const idleBorder = idleBg;
  const idleColor  = '#fff';
  const hoverBg     = hoverDanger ? '#991b1b' : 'var(--color-accent-hover)';
  const hoverBorder = hoverBg;
  const hoverColor  = '#fff';

  return (
    <button
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        border: `1px solid ${hover ? hoverBorder : idleBorder}`,
        cursor: 'pointer',
        background: hover ? hoverBg : idleBg,
        color: hover ? hoverColor : idleColor,
        transition: 'background 120ms, color 120ms, border-color 120ms, transform 120ms',
        transform: hover ? 'translateY(-1px)' : 'none',
        boxShadow: hover ? '0 2px 6px rgba(15,23,32,0.12)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

/** Hover-reveal action buttons for compact rows (fade in over a gradient). */
export function GradientOverlayActions({
  visible,
  bgColor,
  onEdit,
  onDelete,
}: {
  visible: boolean;
  bgColor: string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        paddingRight: 14,
        paddingLeft: 56,
        background: `linear-gradient(90deg, transparent 0%, ${bgColor} 32%, ${bgColor} 100%)`,
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 150ms',
        zIndex: 5,
      }}
    >
      <IconButton title="Edit" onClick={onEdit} size={30}>
        <Pencil size={15} />
      </IconButton>
      <IconButton title="Delete" onClick={onDelete} hoverDanger size={30}>
        <Trash2 size={15} />
      </IconButton>
    </div>
  );
}

/** Generic comfortable/compact (or any) density switch — segmented icon buttons. */
export function ViewModeSwitch<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { id: T; tip: string; Icon: ComponentType<{ size?: number }> }[];
}) {
  return (
    <div style={{ display: 'inline-flex', padding: 3, background: 'var(--color-panel-alt)', border: '1px solid var(--color-border)', borderRadius: 7, gap: 1 }}>
      {options.map((o) => {
        const isActive = value === o.id;
        return (
          <button
            key={o.id}
            title={o.tip}
            aria-label={o.tip}
            aria-pressed={isActive}
            onClick={() => onChange(o.id)}
            style={{
              width: 30,
              height: 26,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 5,
              border: 'none',
              cursor: 'pointer',
              background: isActive ? 'var(--color-accent)' : 'transparent',
              color: isActive ? '#fff' : 'var(--color-text-mid)',
              transition: 'background 120ms, color 120ms',
            }}
          >
            <o.Icon size={15} />
          </button>
        );
      })}
    </div>
  );
}

/** Prominent pagination — labelled Prev/Next plus numbered page buttons.
 *  Renders nothing when there is only one page. */
export function PaginationControls({
  currentPage,
  totalPages,
  onPage,
}: {
  currentPage: number;
  totalPages: number;
  onPage: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  // Compact page list: 1 … around-current … last  (max ~7 entries)
  const pages: (number | '…')[] = [];
  if (totalPages <= 7) {
    for (let p = 1; p <= totalPages; p++) pages.push(p);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push('…');
    for (let p = Math.max(2, currentPage - 1); p <= Math.min(totalPages - 1, currentPage + 1); p++) pages.push(p);
    if (currentPage < totalPages - 2) pages.push('…');
    pages.push(totalPages);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <PageNavButton
        label="Prev"
        Icon={ChevronLeft}
        iconFirst
        disabled={currentPage <= 1}
        onClick={() => onPage(currentPage - 1)}
      />
      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`e${i}`} style={{ padding: '0 4px', color: 'var(--color-text-dim)', fontSize: 13 }}>…</span>
        ) : (
          <PageNumberButton key={p} page={p} active={p === currentPage} onClick={() => onPage(p)} />
        ),
      )}
      <PageNavButton
        label="Next"
        Icon={ChevronRight}
        disabled={currentPage >= totalPages}
        onClick={() => onPage(currentPage + 1)}
      />
    </div>
  );
}

function PageNavButton({
  label, Icon, iconFirst, disabled, onClick,
}: {
  label: string; Icon: ComponentType<{ size?: number }>; iconFirst?: boolean; disabled?: boolean; onClick: () => void;
}) {
  const [hover, setHover] = useState(false);
  const active = hover && !disabled;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, height: 32, padding: '0 10px',
        borderRadius: 7,
        border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
        background: 'var(--color-panel)',
        color: disabled ? 'var(--color-text-dim)' : active ? 'var(--color-accent)' : 'var(--color-text-mid)',
        fontSize: 12.5, fontWeight: 600, fontFamily: 'var(--font-body)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'border-color 120ms, color 120ms',
      }}
    >
      {iconFirst && <Icon size={15} />}
      {label}
      {!iconFirst && <Icon size={15} />}
    </button>
  );
}

function PageNumberButton({ page, active, onClick }: { page: number; active: boolean; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-current={active ? 'page' : undefined}
      style={{
        minWidth: 32, height: 32, padding: '0 6px', borderRadius: 7,
        border: `1px solid ${active ? 'var(--color-accent)' : hover ? 'var(--color-accent)' : 'var(--color-border)'}`,
        background: active ? 'var(--color-accent)' : 'var(--color-panel)',
        color: active ? '#fff' : hover ? 'var(--color-accent)' : 'var(--color-text-mid)',
        fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-body)',
        cursor: 'pointer',
        transition: 'background 120ms, border-color 120ms, color 120ms',
      }}
    >
      {page}
    </button>
  );
}

/** Small prev/next pagination button. */
export function PaginationButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 26,
        height: 26,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--color-border)',
        background: 'var(--color-panel)',
        color: disabled ? 'var(--color-text-dim)' : 'var(--color-text-mid)',
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}
